import * as AdrDomain from "@/domain/adr"
import {
  DEFAULT_LONG_FORMAT,
  getDateInUserLocale
} from "@/helpers/date.helpers"
import { CveList } from "@/pages/TriagePage/components/pages/AdrPage/components/DetailsTabAdr/components/CveList"
import { FunctionList } from "@/pages/TriagePage/components/pages/AdrPage/components/DetailsTabAdr/components/FunctionList"
import { PackagesList } from "@/pages/TriagePage/components/pages/AdrPage/components/DetailsTabAdr/components/PackagesList"

import type { AdrIncident, AIIncident, MitreIncident } from "@/domain/adr"
import type { TFunction } from "@/hooks/useTranslation"

interface Values {
  why?: null | string
  label?: string
  shouldHideEmpty?: boolean
}

const getProcessCommandLine = (
  incident: MitreIncident | AIIncident,
  values?: Values
) => ({
  key: "processCommandLine",
  label: "Process Command Line",
  value: incident.what.processCommandLine,
  why: "Shows how the process was started, offering context on its configuration and arguments.",
  ...values
})

const getParentProcessName = (
  incident: MitreIncident | AIIncident,
  values?: Values
) => ({
  key: "parentProcessName",
  label: "Parent Process Name",
  value: incident.what.parentProcessName,
  why: "Shows how the process was started, offering context on its configuration and arguments.",
  ...values
})

const getParentProcessCommandLine = (
  incident: MitreIncident | AIIncident,
  values?: Values
) => ({
  key: "parentProcessCommandLine",
  label: "Parent Process Command Line",
  value: incident.what.parentProcessCommandLine,
  why: "Shows how the process was started, offering context on its configuration and arguments.",
  ...values
})

const getParentProcessExecutable = (
  incident: MitreIncident,
  values?: Values
) => ({
  key: "parentProcessExecutable",
  label: "Parent Process Executable",
  value: incident.what.parentProcessExecutable,
  ...values
})

const getParentProcessExecutablePath = (
  incident: MitreIncident,
  values?: Values
) => ({
  key: "parentProcessExecutablePath",
  label: "Parent Executable Path",
  value: incident.what.parentProcessExecutablePath,
  ...values
})

const getExecutablePath = (
  incident: MitreIncident | AIIncident,
  values?: Values
) => ({
  key: "processExecutablePath",
  label: "Process Executable Path",
  value: incident.what.processExecutablePath,
  why: "The full path to the executable on disk. This helps verify the process is legitimate and not a malicious binary masquerading with the same name.",
  ...values
})

const getActionTaken = (incident: AIIncident, values?: Values) => ({
  key: "actionTaken",
  label: "Action Taken",
  value: incident.enforcement
    ? `Blocked by policy "${incident.enforcement.policyName}"`
    : undefined,
  why: "The runtime policy stopped the connection before any data left the pod.",
  ...values
})

const getDestinationHost = (incident: AIIncident, values?: Values) => ({
  key: "destinationHost",
  label: "Destination Host",
  value: incident.what.destinationHost,
  why: "The host the agent tried to reach. Compare it with the destinations observed for this agent.",
  ...values
})

const getDestinationUrl = (incident: AIIncident, values?: Values) => ({
  key: "destinationUrl",
  label: "Destination URL",
  value: incident.what.destinationUrl,
  ...values
})

const getHttpMethod = (incident: AIIncident, values?: Values) => ({
  key: "httpMethod",
  label: "HTTP Method",
  value: incident.what.httpMethod,
  ...values
})

const getPortAndProtocol = (incident: AIIncident, values?: Values) => ({
  key: "portProtocol",
  label: "Port / Protocol",
  value:
    incident.what.destinationPort && incident.what.protocol
      ? `${incident.what.destinationPort} / ${incident.what.protocol}`
      : undefined,
  ...values
})

const getBytesAttempted = (incident: AIIncident, values?: Values) => ({
  key: "bytesAttempted",
  label: "Bytes Sent",
  value: incident.what.bytesAttempted,
  why: "A blocked connection never completes the handshake, so no payload reaches the destination.",
  ...values
})

const getToolName = (incident: AIIncident, values?: Values) => ({
  key: "toolName",
  label: "Agent Tool",
  value: incident.what.toolName,
  why: "The agent tool that issued the request. The tool is legitimate; the destination came from the model.",
  ...values
})

const getPromptSnippet = (incident: AIIncident, values?: Values) => ({
  key: "promptSnippet",
  label: "Triggering Instruction",
  value: incident.what.promptSnippet,
  why: "The content that steered the model. Use it to find where the instruction was planted.",
  ...values
})

export const getParentCommandLine = (
  incident: MitreIncident | AIIncident,
  values?: Values
) => ({
  key: "parentCommandLine",
  label: "Parent Command Line",
  value: incident.what.parentCommandLine,
  ...values
})

export const getSystemCallTrigger = (
  incident: MitreIncident,
  values?: Values
) => ({
  key: "systemCallTriggered",
  label: "System Call Triggered",
  value: incident.what.systemCallTriggred,
  why: "Shows how the process was started, offering context on its configuration and arguments.",
  ...values
})

export const getUserId = (
  incident: MitreIncident | AIIncident,
  values?: Values
) => ({
  key: "userId",
  label: "User ID",
  value: incident.what.userId,
  why: "The user ID inside the container that the shell is running as. A UID of 0 indicates root privileges.",
  ...values
})

export const getUserLoginId = (
  incident: MitreIncident | AIIncident,
  values?: Values
) => ({
  key: "userLoginId",
  label: "User Login ID",
  value: incident.what.userLoginId,
  why: "Attributes the activity to the account behind any privilege changes.",
  ...values
})

export const getUserName = (
  incident: MitreIncident | AIIncident,
  values?: Values
) => ({
  key: "userName",
  label: "User Name",
  value: incident.what.userName,
  why: "The non-interactive system account that was used to run the command.",
  ...values
})

export const getTerminal = (
  incident: MitreIncident | AIIncident,
  values?: Values
) => ({
  key: "terminal",
  label: "Terminal",
  value: incident.what.terminal,
  why: "A non-zero value confirms this was an interactive session.",
  ...values
})

export const getUsersWithTerminal = (
  incident: MitreIncident | AIIncident,
  values?: Values
) => [
  getUserId(incident, values),
  getUserLoginId(incident, values),
  getTerminal(incident, values)
]

export const getProcessName = (incident: AdrIncident, values?: Values) => ({
  key: "processName",
  label: "Process Name",
  value: incident.what.processName,
  why: "The name of the shell process that was spawned (e.g., bash, sh).",
  ...values
})

export const getPackages = (
  incident: AdrIncident,
  t: TFunction,
  values?: Values
) => ({
  t,
  key: "packages",
  label: t("general.package", {
    count: AdrDomain.getCountCve(incident)
  }),
  record: incident,
  value: PackagesList,
  ...values
})

export const getCVEs = (
  incident: AdrIncident,
  t: TFunction,
  values?: Values
) => ({
  t,
  key: "cves",
  label: t("general.cveText", {
    count: AdrDomain.getCountCve(incident)
  }),
  record: incident,
  value: CveList,
  ...values
})

export const getVulnerableFunctions = (
  incident: AdrIncident,
  t: TFunction,
  values?: Values
) => ({
  t,
  key: "vulnerable-functions",
  label: t("general.vulnerableFunction", {
    count: AdrDomain.getCountCve(incident)
  }),
  record: incident,
  value: FunctionList,
  ...values
})

export const getFilePath = (incident: MitreIncident, values?: Values) => ({
  key: "filePath",
  label: "File Path",
  value: incident.what.filePath,
  ...values
})

export const getFileModifiedAt = (
  incident: MitreIncident,
  values?: Values
) => ({
  key: "fileModifiedAt",
  label: "File Modified At",
  value: incident.what.fileModifiedAtUserLocale,
  ...values
})

export const getFileHash = (incident: MitreIncident, values?: Values) => ({
  key: "fileHash",
  label: "File Hash (SHA256)",
  value: incident.what.fileHash,
  ...values
})

export const getVmBootTime = (incident: MitreIncident, values?: Values) => ({
  key: "vmBootTime",
  label: "VM Boot Time",
  value: incident.what.vmBootTimeUserLocale,
  ...values
})

export const getContainerStartTime = (
  incident: MitreIncident,
  values?: Values
) => {
  if (AdrDomain.isVM(incident)) return getVmBootTime(incident, values)

  return {
    key: "containerStartTime",
    label: "Container Start Time",
    value: incident.what.containerStartTimeUserLocale,
    ...values
  }
}

export const getProtectionFlags = (
  incident: MitreIncident,
  values?: Values
) => ({
  key: "protectionFlags",
  label: "Protection Flags",
  value: incident.what.protFlagsHuman,
  ...values
})

export const getSystemCallName = (
  incident: MitreIncident,
  values?: Values
) => ({
  key: "systemCall",
  label: "Syscall",
  value: incident.what.syscallName,
  why: "mmap = new region; mprotect = permission flip. Reveals injection technique.",
  ...values
})

export const getProcessStartTime = (incident: MitreIncident) => ({
  key: "processStartTime",
  label: "Process Start Time",
  value: incident.what.processStartTime
})

export const getWhatItems = (incident: AdrIncident, t: TFunction) => {
  if (!incident) return []

  const { type, what, detectedAt } = incident
  const time = {
    key: "time",
    label: "Time",
    value: detectedAt && getDateInUserLocale(detectedAt, DEFAULT_LONG_FORMAT)
  }

  switch (type) {
    case "OS_COMMAND_INJECTION":
    case "CODE_INJECTION":
    case "UNSAFE_DESERIALIZATION":
      return [
        time,
        {
          key: "callerProcess",
          label: "Caller Process",
          value: what.callerProcess,
          why: "Identifies the specific application process that was exploited. It's the starting point of the investigation, telling you which service contains the code vulnerability that needs to be fixed."
        },
        {
          key: "processArguments",
          label: "Process Arguments",
          value: what.processArguments
        },
        {
          key: "calledFunction",
          label: "Called Function",
          value: what.calledFunction,
          why: "This is a function in your application that was manipulated to execute the malicious command. This includes the suspicious arguments."
        },
        {
          key: "sensitiveSyscall",
          label: "Sensitive Syscall",
          value: what.sensitiveSyscall
        },
        {
          key: "binaryExecuted",
          label: "Binary Executed",
          value: what.binaryExecuted,
          why: "The binary that the attacker successfully executed on the server."
        },
        getProcessName(incident)
      ]
    case "DIRECTORY_TRAVERSAL":
      return [
        time,
        getProcessName(incident, { why: undefined }),
        getProcessCommandLine(incident),
        getParentProcessName(incident),
        getUserId(incident),
        {
          key: "filePathAccessed",
          label: "File Path Accessed",
          value: what.filePathAccessed,
          why: "Shows how the process was started, offering context on its configuration and arguments."
        },
        {
          key: "traversalPath",
          label: "Traversal Path",
          value: what.traversalPath,
          why: "Shows how the process was started, offering context on its configuration and arguments."
        }
      ]
    case "PTRACE_ATTACH":
      return [
        time,
        getProcessName(incident, {
          why: "The primary indicator of intent. An expected name like gdb or strace suggests debugging, while an unknown name is highly suspicious."
        }),
        getProcessCommandLine(incident, {
          why: "Shows the full command and arguments used, helping to confirm the tool's purpose (e.g., gdb -p 1234)."
        }),
        getExecutablePath(incident),
        getParentProcessName(incident, { why: undefined })
      ]
    case "PTRACE_ANTI_DEBUG":
      return [
        time,
        getProcessName(incident, {
          why: "The most critical piece of evidence. The name of the process being traced, which could be your application or a malicious binary. If this is a known debugger (gdb, dlv), the activity is likely benign. If not, it is highly suspicious."
        }),
        getProcessCommandLine(incident, { why: null }),
        getExecutablePath(incident, { why: null }),
        getParentProcessName(incident, { why: null })
      ]
    case "ANOMALOUS_SENSITIVE_FILE_READ":
      return [
        time,
        getProcessName(incident, { why: undefined }),
        getProcessCommandLine(incident, { why: undefined }),
        getExecutablePath(incident),
        getParentProcessName(incident, { why: undefined }),
        getParentCommandLine(incident),
        {
          key: "fileAccessed",
          label: "File Accessed",
          value: what.fileAccessed,
          why: "The sensitive file that was unexpectedly read, potentially containing credentials or configuration secrets."
        },
        getSystemCallTrigger(incident, { why: undefined }),
        getUserId(incident),
        getUserLoginId(incident)
      ]
    case "CREDENTIAL_HARVESTING_SEARCH":
      return [
        time,
        getProcessName(incident),
        getProcessCommandLine(incident),
        getExecutablePath(incident, { why: undefined }),
        getParentProcessName(incident),
        ...getUsersWithTerminal(incident)
      ]
    case "UNEXPECTED_SHELL_SPAWNED":
      return [
        time,
        getProcessName(incident),
        getProcessCommandLine(incident),
        getParentProcessCommandLine(incident),
        getParentProcessExecutable(incident),
        ...getUsersWithTerminal(incident, { why: null })
      ]
    case "INTERACTIVE_SESSION_SYSTEM_ACCOUNT":
      return [
        time,
        getProcessName(incident, { why: undefined }),
        getProcessCommandLine(incident),
        getExecutablePath(incident, { why: undefined }),
        getParentProcessName(incident),
        getUserName(incident),
        ...getUsersWithTerminal(incident)
      ]
    case "INTERACTIVE_SHELL_IN_CONTAINER":
      return [
        time,
        getProcessName(incident),
        getProcessCommandLine(incident, { why: undefined }),
        ...getUsersWithTerminal(incident)
      ]
    case "FILELESS_EXECUTION":
      return [
        time,
        getProcessName(incident),
        getProcessCommandLine(incident, { why: undefined }),
        getParentProcessName(incident),
        getParentProcessCommandLine(incident, { why: undefined }),
        ...getUsersWithTerminal(incident, { why: undefined })
      ]

    case "POTENTIAL_EXPLOIT_CVE":
      return [
        time,
        getCVEs(incident, t),
        getPackages(incident, t),
        getVulnerableFunctions(incident, t)
      ]

    case "EXPOSURE_TO_NEWLY_PUBLISHED_CVE":
      return [
        time,
        getCVEs(incident, t),
        {
          key: "publish-date",
          label: "CVE Published Date",
          value:
            incident.what.vulnerabilitiesMeta?.cve?.cvePublishedDateUserLocale
        },
        getPackages(incident, t),
        getVulnerableFunctions(incident, t)
      ]
    case "PROMPT_INJECTION_WITH_SENSITIVE_OUTPUT":
    case "LLM_MISUSE_TRIGGERED_UNEXPECTED_SHELL_ATTEMPT":
    case "POISONED_LOG_LINE_TRIGGERED_UNAPPROVED_PRODUCTION_SHELL":
      return [
        time,
        getProcessName(incident, { why: undefined }),
        getProcessCommandLine(incident, { why: undefined }),
        getParentProcessName(incident, { why: undefined }),
        getParentProcessCommandLine(incident, { why: undefined }),
        ...getUsersWithTerminal(incident, { why: undefined })
      ]
    case "UNSANITIZED_PROMPT_TRIGGERED_AI_AGENT_CODE_EXECUTION":
      return [
        time,
        getProcessName(incident, { why: undefined }),
        getProcessCommandLine(incident, { why: undefined }),
        getParentProcessName(incident, { why: undefined }),
        getParentProcessCommandLine(incident, { why: undefined })
      ]
    case "UNSANCTIONED_NETWORK_ACCESS":
      return [
        time,
        getHeading("Enforcement"),
        getActionTaken(incident),
        getHeading("Network"),
        getDestinationHost(incident),
        getDestinationUrl(incident),
        getHttpMethod(incident),
        getPortAndProtocol(incident),
        getBytesAttempted(incident),
        getHeading("Agent"),
        getToolName(incident),
        getPromptSnippet(incident),
        getProcessName(incident, { why: undefined }),
        getProcessCommandLine(incident, { why: undefined }),
        getParentProcessName(incident, { why: undefined }),
        getParentProcessCommandLine(incident, { why: undefined })
      ]
    case "CODE_TAMPERING_LIBRARY_LOAD":
      return [
        time,
        getProcessName(incident, {
          why: "Identifies which process loaded the suspicious library — a web server or application loading a modified library is a strong compromise signal."
        }),
        getExecutablePath(incident, {
          why: "The full binary path reveals whether this is a legitimate system binary (e.g., /usr/sbin/nginx) or something unexpected dropped into a temp directory."
        }),
        getProcessCommandLine(incident, { why: undefined }),
        getUserId(incident, {
          why: "Indicates whether the process ran as root or as an application user — root-level library loading is a higher-risk signal and may indicate privilege escalation."
        }),
        getUserLoginId(incident, {
          why: "The original login user, even after su or privilege changes — critical for attributing the activity to a specific human or service account that initiated the session."
        }),
        getFilePath(incident, {
          why: "The location of the modified library — libraries in system paths (/lib, /usr/lib) suggest a more sophisticated attack than temporary directories."
        }),
        getFileModifiedAt(incident, {
          why: "This timestamp is after the container was already running, meaning the library was introduced or changed post-deployment — which should not happen in an immutable container."
        }),
        getFileHash(incident, {
          why: "Enables immediate lookup in threat intelligence platforms (e.g., VirusTotal) to determine if the library is a known malicious payload."
        }),
        getContainerStartTime(incident, { why: undefined })
      ]
    case "CODE_TAMPERING_PROCESS_EXECUTION":
      return [
        time,
        getProcessName(incident, {
          why: "An unexpected or impersonated process name is a strong compromise signal."
        }),
        {
          key: "binaryPath",
          label: "Binary Path",
          value: incident.what.filePath,
          why: "Where the tampered binary lives - the path itself distinguishes a patched legitimate tool from a dropped one."
        },
        getProcessCommandLine(incident),
        getUserId(incident, {
          why: "Root-level execution raises severity and may indicate privilege escalation."
        }),
        getUserLoginId(incident),
        getParentProcessName(incident, {
          why: "What spawned the binary — points to the attack path (drop-and-execute) or the container's normal caller (patch-in-place)."
        }),
        getParentProcessExecutablePath(incident, {
          why: "Shows whether the spawner itself is legitimate or attacker-controlled."
        }),
        getParentCommandLine(incident, {
          why: "Reveals how the binary was triggered."
        }),
        getFileModifiedAt(incident, {
          why: "Proves the binary was added or modified post-deployment - which shouldn't happen in an immutable container."
        }),
        getFileHash(incident, {
          why: "Enables a threat-intel lookup to identify known malicious payloads."
        }),
        getContainerStartTime(incident)
      ]
    case "CODE_TAMPERING_SCRIPT_LOAD":
      return [
        time,
        getProcessName(incident, {
          label: "Process Name (Interpreter)",
          why: "Which interpreter was used reveals the attacker's tooling choice and helps hunt for variants (Python reverse shells, PHP webshells, Bash loaders)."
        }),
        getExecutablePath(incident, {
          why: "Confirms which interpreter binary was invoked - an unusual or unexpected interpreter location can indicate a dropped interpreter rather than the image's standard one."
        }),
        getProcessCommandLine(incident, {
          why: "Typically contains the script path and arguments - the clearest single-field view of what the attacker asked the interpreter to do."
        }),
        getUserId(incident, {
          why: "Root-level interpreter execution raises severity and may indicate privilege escalation."
        }),
        getUserLoginId(incident),
        getParentProcessName(incident, {
          why: "What invoked the interpreter - the start of the attack chain for this rule."
        }),
        getParentProcessExecutablePath(incident, {
          why: "Shows whether the spawner itself is legitimate (e.g., a web server) or attacker-controlled."
        }),
        getParentCommandLine(incident, {
          why: "Reveals how the interpreter was invoked (full argv of the parent)."
        }),
        {
          key: "scriptPath",
          label: "Script Path",
          value: incident.what.filePath
        },
        {
          key: "scriptLanguage",
          label: "Script Language",
          value: incident.what.scriptLanguage,
          why: "Language mismatch with the workload (e.g. Python in a Node-only image) or an unexpected language is a compromise signal."
        },
        getFileModifiedAt(incident, {
          why: "Proves the script was added or modified post-deployment - which shouldn't happen in an immutable container."
        }),
        getFileHash(incident, {
          why: "Enables threat-intel lookup to identify known malicious payloads and distinguishes content changes from metadata-only changes."
        }),
        getContainerStartTime(incident)
      ]
    case "EXEC_MEMORY_NON_JIT":
      return [
        time,
        getProcessName(incident, {
          why: "Compiled binaries (Go/Rust/C/C++) should never allocate executable memory at runtime."
        }),
        getExecutablePath(incident, {
          why: "Unknown or unexpected binaries in unusual locations raise severity."
        }),
        getProcessCommandLine(incident, {
          why: "Identifies the workload and whether it was behaving normally at the time."
        }),
        getUserId(incident, {
          why: "Root processes are higher risk — injected code inherits full privileges."
        }),
        getSystemCallName(incident),
        getProtectionFlags(incident, {
          why: "RWX is the most dangerous — no extra step needed to run injected code."
        }),
        {
          key: "allocationType",
          label: "Allocation Type",
          value: incident.what.allocType,
          why: "How long the process was running before the allocation — longer uptimes strengthen the signal (not a startup artifact)."
        },
        {
          key: "processAge",
          label: "Process Age",
          value: incident.what.processAge,
          why: "rwx_allocation = simultaneous write+execute, a W^X violation and the strongest signal. anonymous_exec = executable memory with no backing file — code created purely in memory. mprotect_flip = existing writable region flipped to executable, the staged write-then-execute pattern."
        },
        getProcessStartTime(incident)
      ]

    case "EXEC_MEMORY_ANOMALOUS_JIT":
      return [
        time,
        getProcessName(incident, {
          why: "Which application is responsible for the anomaly."
        }),
        getExecutablePath(incident, {
          why: "Unexpected binaries in unusual locations raise severity."
        }),
        getProcessCommandLine(incident),
        getUserId(incident, {
          why: "Root processes are higher risk - injected code inherits full privileges."
        }),
        {
          key: "runtime",
          label: "Runtime",
          value: incident.what.runtimeName,
          why: "Determines which JIT library is expected in the call stack."
        },
        getSystemCallName(incident, {
          why: "mmap = new region; mprotect = permission flip."
        }),
        getProtectionFlags(incident, {
          why: "Simultaneous RWX is suspicious - modern JIT engines use W^X (allocate RW, then flip to RX)."
        }),
        {
          key: "expectedJitLibrary",
          label: "Expected JIT Library",
          value: incident.what.expectedJitLibrary,
          why: "Its absence from the call stack is the core anomaly."
        },
        {
          key: "callerLibrary",
          label: "Caller Library",
          value: incident.what.callerLibrary,
          why: "Known extensions may be benign and allowlistable; unknown callers are more suspicious."
        },
        {
          key: "capturedStackDepth",
          label: "Captured Stack Depth",
          value: incident.what.capturedStackDepth,
          why: "How many frames were captured."
        },
        {
          key: "maximumStackDepth",
          label: "Maximum Stack Depth",
          value: incident.what.maxStackDepth,
          why: "If captured depth equals this, the stack may be truncated."
        },
        {
          key: "rwxSimultaneous",
          label: "RWX Simultaneous",
          value: `${incident.what.rwxSimultaneous}`,
          why: "Confirms whether W^X was violated in this specific operation."
        },
        {
          key: "allocationSizeCategory",
          label: "Allocation Size Category",
          value: incident.what.allocationSizeCategory,
          why: "JIT compilers emit a few KB at a time; >1MB is atypical."
        },
        getProcessStartTime(incident)
      ]

    default:
      return []
  }
}

const getEnvironmentFromWhere = (where: AdrIncident["where"]) =>
  ({
    key: "environment",
    label: "Environment",
    value: where.environment,
    why: "A key indicator of intent. An event in a prod environment is significantly more critical than in dev or staging."
  }) as const

export const getDefaultWhere = (where: AdrIncident["where"]) => [
  { key: "imageTag", label: "Image:Tag", value: where.imageTag },
  {
    key: "namespace",
    label: "Namespace",
    value: where.namespace,
    why: "Helps determine context; debugging in a production namespace is more suspicious than in a development one."
  },
  getEnvironmentFromWhere(where),
  {
    key: "deploymentName",
    label: "Deployment Name",
    value: where.deploymentName
  },
  {
    key: "podName",
    label: "Pod Name",
    value: where.podName
  },
  {
    key: "containerId",
    label: "Container ID",
    value: where.containerId
  },
  {
    key: "containerName",
    label: "Container Name",
    value: where.containerName
  }
]

const getImageTag = (where: AdrIncident["where"], values?: Values) =>
  ({
    key: "imageTag",
    label: "Image",
    value: where.imageTag,
    ...values
  }) as const

const getContainerId = (where: AdrIncident["where"], values?: Values) =>
  ({
    key: "containerId",
    label: "Container ID",
    value: where.containerId,
    ...values
  }) as const

const getContainerName = (where: AdrIncident["where"], values?: Values) =>
  ({
    key: "containerName",
    label: "Container Name",
    value: where.containerName,
    ...values
  }) as const

const getNameSpace = (where: AdrIncident["where"], values?: Values) =>
  ({
    key: "namespace",
    label: "Namespace",
    value: where.namespace,
    why: "Helps determine context; debugging in a production namespace is more suspicious than in a development one.",
    ...values
  }) as const

const getDeploymentName = (where: AdrIncident["where"], values?: Values) =>
  ({
    key: "deploymentName",
    label: "Deployment Name",
    value: where.deploymentName,
    ...values
  }) as const

const getPodName = (where: AdrIncident["where"], values?: Values) =>
  ({
    key: "podName",
    label: "Pod Name",
    value: where.podName,
    ...values
  }) as const

const getServiceName = (where: AdrIncident["where"], values?: Values) =>
  ({
    key: "serviceName",
    label: "Service Name",
    value: where.serviceName,
    ...values
  }) as const

const getTaskName = (where: AdrIncident["where"], values?: Values) =>
  ({
    key: "taskName",
    label: "Task Name",
    value: where.taskName,
    ...values
  }) as const

const getInstanceId = (where: AdrIncident["where"], values?: Values) =>
  ({
    key: "instanceId",
    label: "Instance ID",
    value: where.instanceId,
    ...values
  }) as const

const getInstanceName = (where: AdrIncident["where"], values?: Values) =>
  ({
    key: "instanceName",
    label: "Instance Name",
    value: where.instanceName,
    ...values
  }) as const

const getAMIId = (where: AdrIncident["where"], values?: Values) =>
  ({
    key: "amiId",
    label: "AMI ID",
    value: where.vmImage,
    ...values
  }) as const

const getAccountId = (where: AdrIncident["where"], values?: Values) =>
  ({
    key: "accountId",
    label: "Account ID",
    value: where.accountId,
    ...values
  }) as const

const getInstanceGroup = (where: AdrIncident["where"], values?: Values) =>
  ({
    key: "instanceGroup",
    label: "Instance Group",
    value: where.instanceGroup,
    ...values
  }) as const

const getOS = (where: AdrIncident["where"], values?: Values) =>
  ({
    key: "os",
    label: "OS",
    value: where.os,
    ...values
  }) as const

export const getHostName = (where: AdrIncident["where"], values?: Values) =>
  ({
    key: "hostname",
    label: "Host Name",
    value: where.hostname,
    ...values
  }) as const

export const getSystemUUID = (where: AdrIncident["where"], values?: Values) =>
  ({
    key: "systemUUID",
    label: "System UUID",
    value: where.systemUuid,
    ...values
  }) as const

export const HEADING_KEY = "___heading"

const getHeading = (label: string) => ({
  key: `${HEADING_KEY}_${label}`,
  label
})

const getContainerHeading = () => getHeading("Container")

const getVMHeading = () => getHeading("VM")

const getContainerSection = (where: AdrIncident["where"]) => [
  getContainerHeading(),
  getImageTag(where),
  getContainerId(where),
  getContainerName(where, { shouldHideEmpty: true })
]

const getVM_EC2_Section = (where: AdrIncident["where"]) => [
  getInstanceId(where),
  getInstanceName(where, { shouldHideEmpty: true }),
  getEnvironmentFromWhere(where),
  getAMIId(where),
  getAccountId(where),
  getInstanceGroup(where, { shouldHideEmpty: true }),
  getOS(where)
]

const getVM_Section = (where: AdrIncident["where"]) => [
  getHostName(where),
  getSystemUUID(where),
  getEnvironmentFromWhere(where),
  getOS(where)
]

export const getWhereItemsValues = (incident?: AdrIncident) => {
  if (!incident) return []

  const instanceType = AdrDomain.getInstanceType(incident)
  const { where } = incident

  switch (instanceType) {
    case "INSTANCE_TYPE_K8S_WORKLOAD":
      return [
        ...getContainerSection(where),
        getHeading("K8S"),
        getEnvironmentFromWhere(where),
        getNameSpace(where),
        getDeploymentName(where),
        getPodName(where)
      ]
    case "INSTANCE_TYPE_ECS_SERVICE":
      return [
        ...getContainerSection(where),
        getHeading("ECS"),
        getEnvironmentFromWhere(where),
        getServiceName(where),
        getTaskName(where)
      ]

    case "INSTANCE_TYPE_CONTAINER":
      switch (where.environmentType) {
        case "CONTAINER_ON_VM_EC2":
          return [
            ...getContainerSection(where),
            getVMHeading(),
            ...getVM_EC2_Section(where)
          ]

        case "CONTAINER_ON_VM":
          return [
            ...getContainerSection(where),
            getVMHeading(),
            ...getVM_Section(where)
          ]

        default:
          return []
      }

    case "INSTANCE_TYPE_EC2":
      return getVM_EC2_Section(where)

    case "INSTANCE_TYPE_ANY_VM":
      return getVM_Section(where)

    default:
      return []
  }
}

export const skipEmptyItems = (
  items: Partial<{
    key: string
    label: string
    value: unknown
    shouldHideEmpty: boolean
  }>[]
) =>
  items.filter(({ value, shouldHideEmpty }) => {
    if (shouldHideEmpty)
      return value !== undefined && value !== null && value !== ""

    return true
  })

export const getWhereItems = (incident?: AdrIncident) =>
  skipEmptyItems(getWhereItemsValues(incident))
